import { getApolloClient } from '@faustwp/core';
import {
  Header,
  Footer,
  HeroSection,
  ServicesSection,
  ValuePropsSection,
  FinalCTASection,
  WHY_IDENTIO_ITEMS,
} from '../../components/sections';
import {
  ServicesAIDifferentiationSection,
  ServicesProcessSection,
  ServicesCaseStudiesSection,
  ServicesClientLogosSection,
} from '../../components/sections/services';
import { SEO } from '../../components';
import { fetchLogoPosts } from '../../lib/logoUtils';
import { fetchPageSettings } from '../../lib/queries/pageSettings';
import { fetchWorkPartnerStories } from '../../lib/workUtils';
import { getCanonicalUrl, buildHreflangTags } from '../../lib/siteUrl';

const FALLBACK_EN = {
  metaTitle: 'Services | Identio',
  metaDescription: 'AI-enhanced software development and UI/UX design for enterprise teams.',
  heroTitle: 'AI-Enhanced Development & Design for Enterprise Teams',
  heroDescription:
    'We integrate seamlessly with your team to deliver exceptional results—powered by AI-assisted development for faster delivery.',
  servicesHeading: 'Our Services',
  servicesSubtitle:
    'Our consulting expertise spans the full spectrum of modern software development.',
  valuePropsTitle: 'Why Leading Organizations Choose Identio',
};

export default function ServicesPage({
  logos = [],
  caseStudies = [],
  servicesSettings = null,
}) {
  const s = servicesSettings ?? {};
  const title = s.seoTitle ?? s.metaTitle ?? s.meta_title ?? FALLBACK_EN.metaTitle;
  const description = s.seoDescription ?? s.metaDescription ?? s.meta_description ?? FALLBACK_EN.metaDescription;
  const heroTitle = s.heroTitle ?? s.hero_title ?? FALLBACK_EN.heroTitle;
  const heroDescription = s.heroDescription ?? s.hero_description ?? FALLBACK_EN.heroDescription;
  const servicesHeading = s.servicesHeading ?? s.services_heading ?? FALLBACK_EN.servicesHeading;
  const servicesSubtitle = s.servicesSubtitle ?? s.services_subtitle ?? FALLBACK_EN.servicesSubtitle;
  const valuePropsTitle = s.valuePropsTitle ?? s.value_props_title ?? FALLBACK_EN.valuePropsTitle;

  return (
    <>
      <SEO title={title} description={description} url={getCanonicalUrl('/services')} alternates={buildHreflangTags('/palvelut', '/services')} />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <HeroSection variant="services" title={heroTitle} description={heroDescription} />
        <ServicesSection
          variant="services"
          servicesHeading={servicesHeading}
          servicesSubtitle={servicesSubtitle}
        />
        <ServicesAIDifferentiationSection />
        <ServicesProcessSection />
        <ServicesCaseStudiesSection caseStudies={caseStudies} />
        <ServicesClientLogosSection logos={logos} />
        <ValuePropsSection title={valuePropsTitle} items={WHY_IDENTIO_ITEMS} columns={3} />
        <FinalCTASection variant="withForm" />
      </main>
      <Footer />
    </>
  );
}

export async function getStaticProps() {
  const client = getApolloClient();
  let servicesSettings = null;
  try {
    servicesSettings = await fetchPageSettings(client, 'services');
  } catch (e) {
    // ACF optional
  }
  try {
    const [logoResult, { partnerStories }] = await Promise.all([
      fetchLogoPosts(client),
      fetchWorkPartnerStories(client),
    ]);
    const { logos } = logoResult ?? {};
    const caseStudies = (partnerStories ?? []).slice(0, 3);
    return {
      props: { logos: logos ?? [], caseStudies, servicesSettings },
      revalidate: 300,
    };
  } catch (err) {
    console.error('[services/index] getStaticProps failed', err);
    return {
      props: { logos: [], caseStudies: [], servicesSettings },
      revalidate: 300,
    };
  }
}
